"use client"
import Script from "next/script"
import { useEffect, useState } from "react"

const GA4_ID   = "G-M06ZSB6ZKK"
const CLR_ID   = "wrg5oiysor"

/**
 * Fire a GA4 conversion event (M6).
 * Safe to call before consent, no-ops if gtag is not yet loaded.
 *
 * Usage:
 *   import { zeTrack } from "@/components/Analytics"
 *   zeTrack("enquiry_sent",   { listing_id: id })
 *   zeTrack("kyc_started")
 *   zeTrack("subscription_started", { plan: "AGENT_SOLO" })
 *   zeTrack("viewing_requested", { listing_id: id })
 */
export function zeTrack(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return
  // GA4
  if (typeof (window as any).gtag === "function") {
    ;(window as any).gtag("event", eventName, params ?? {})
  }
  // Facebook Pixel (L1), map common events to standard FB events
  if (typeof (window as any).fbq === "function") {
    const fbStandard: Record<string, string> = {
      enquiry_sent:           "Lead",
      viewing_requested:      "Schedule",
      kyc_started:            "InitiateCheckout",
      subscription_started:   "InitiateCheckout",
      subscription_completed: "Purchase",
    }
    const fbEvent = fbStandard[eventName] ?? "CustomEvent"
    if (fbStandard[eventName]) {
      ;(window as any).fbq("track", fbEvent, params ?? {})
    } else {
      ;(window as any).fbq("trackCustom", eventName, params ?? {})
    }
  }
}

export default function Analytics() {
  const [consented, setConsented] = useState(false)
  const fbPixelId = process.env.NEXT_PUBLIC_FB_PIXEL_ID

  useEffect(() => {
    function check() {
      setConsented(localStorage.getItem("ze_cookie_consent") === "true")
    }
    check()
    window.addEventListener("ze:consent", check)
    return () => window.removeEventListener("ze:consent", check)
  }, [])

  if (!consented) return null

  return (
    <>
      {/* Google Analytics 4 */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-config" strategy="afterInteractive">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA4_ID}');
      `}</Script>

      {/* Microsoft Clarity */}
      <Script id="clarity-init" strategy="afterInteractive">{`
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${CLR_ID}");
      `}</Script>

      {/* Facebook Pixel (L1), only if NEXT_PUBLIC_FB_PIXEL_ID is set */}
      {fbPixelId && (
        <>
          <Script id="fb-pixel-init" strategy="afterInteractive">{`
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
            n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window,document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${fbPixelId}');
            fbq('track', 'PageView');
          `}</Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${fbPixelId}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}
    </>
  )
}
